import { axiosInstance } from "../services/apiService";
import type { AxiosRequestConfig } from "axios";

interface V2SuccessBody<TData = unknown> {
  success: true;
  data: TData;
}

interface LegacyBody<TData = unknown> {
  status?: boolean;
  data?: TData;
}

type ResponseBody<TData = unknown> = V2SuccessBody<TData> | LegacyBody<TData>;

function isV2Success<TData>(body: unknown): body is V2SuccessBody<TData> {
  return (
    typeof body === "object" &&
    body !== null &&
    "success" in body &&
    (body as V2SuccessBody<TData>).success === true
  );
}

function isLegacyWrapped<TData>(body: unknown): body is LegacyBody<TData> {
  return (
    typeof body === "object" &&
    body !== null &&
    ("status" in body || "data" in body) &&
    !("success" in body)
  );
}

export const fetcher = async <TResponse, TRequest = undefined>(
  url: string,
  method = "GET",
  requestData?: TRequest
): Promise<TResponse> => {
  try {
    const config: AxiosRequestConfig = {
      method,
      url,
      ...(requestData && { data: requestData }),
    };

    const response = await axiosInstance(config);
    const body = response.data as ResponseBody<TResponse> | TResponse;

    // v2 format: { success: true, data: ... } - unwrap the data
    if (isV2Success<TResponse>(body)) {
      return body.data;
    }

    // Legacy format with status/data wrapper - unwrap the data
    if (isLegacyWrapped<TResponse>(body) && body.data !== undefined) {
      return body.data as TResponse;
    }

    // Raw data (no wrapper) - return as-is
    return body as TResponse;
  } catch (error) {
    // The axios interceptor already converts errors to Error objects,
    // but handle edge cases for safety
    if (error instanceof Error) {
      throw error;
    }

    if (typeof error === "string") {
      throw new Error(error);
    }

    throw new Error("An unknown error occurred");
  }
};
